import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from './ui/card';
import { Play, Pause, SkipForward, Monitor } from 'lucide-react';
import { Button } from './ui/button';

const LivePreview = ({ content = [], slideDuration = 5 }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef(null);
  const progressRef = useRef(null);

  const filteredContent = content.filter(item => item.type === 'image' || item.type === 'video');

  useEffect(() => {
    if (!playing || filteredContent.length === 0) {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (progressRef.current) clearInterval(progressRef.current);
      return;
    }

    const currentItem = filteredContent[currentIndex];
    const itemDuration = (currentItem?.duration || slideDuration) * 1000;

    setProgress(0);
    let elapsed = 0;
    const interval = 50;

    progressRef.current = setInterval(() => {
      elapsed += interval;
      setProgress((elapsed / itemDuration) * 100);
      if (elapsed >= itemDuration) {
        clearInterval(progressRef.current);
      }
    }, interval);

    timerRef.current = setTimeout(() => {
      setCurrentIndex(prev => (prev + 1) % filteredContent.length);
    }, itemDuration);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (progressRef.current) clearInterval(progressRef.current);
    };
  }, [currentIndex, playing, filteredContent.length, slideDuration]);

  const skip = () => {
    if (filteredContent.length === 0) return;
    setCurrentIndex(prev => (prev + 1) % filteredContent.length);
  };

  if (filteredContent.length === 0) {
    return (
      <Card className="bg-slate-900 border-slate-800" data-testid="live-preview">
        <CardContent className="p-6">
          <h3 className="text-white font-semibold flex items-center gap-2 mb-4">
            <Monitor className="w-5 h-5 text-cyan-400" /> Live Preview
          </h3>
          <div className="bg-slate-950 rounded-lg aspect-video flex items-center justify-center">
            <p className="text-slate-500 text-sm">Add content to see preview</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const currentItem = filteredContent[currentIndex] || filteredContent[0];

  return (
    <Card className="bg-slate-900 border-slate-800" data-testid="live-preview">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-semibold flex items-center gap-2">
            <Monitor className="w-5 h-5 text-cyan-400" /> Live Preview
          </h3>
          <div className="flex items-center gap-2">
            <span className="text-slate-400 text-xs">
              {currentIndex + 1}/{filteredContent.length}
            </span>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 w-7 p-0 text-slate-400 hover:text-white"
              onClick={() => setPlaying(!playing)}
            >
              {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 w-7 p-0 text-slate-400 hover:text-white"
              onClick={skip}
            >
              <SkipForward className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <div className="bg-black rounded-lg aspect-video overflow-hidden relative">
          {currentItem.type === 'image' ? (
            <img
              src={currentItem.url}
              alt={currentItem.description || 'Preview'}
              className="w-full h-full object-contain"
              onError={(e) => { e.target.src = ''; }}
            />
          ) : currentItem.url?.includes('youtube.com') || currentItem.url?.includes('youtu.be') ? (
            <iframe
              src={currentItem.url}
              className="w-full h-full"
              frameBorder="0"
              allow="autoplay"
              title="Video preview"
            />
          ) : (
            <video
              src={currentItem.url}
              className="w-full h-full object-contain"
              muted
              autoPlay
              loop
            />
          )}
          {/* Progress bar */}
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-slate-800">
            <div
              className="h-full bg-cyan-500 transition-all duration-50"
              style={{ width: `${Math.min(progress, 100)}%` }}
            />
          </div>
        </div>
        {currentItem.description && (
          <p className="text-slate-400 text-xs mt-2 truncate">{currentItem.description}</p>
        )}
        {currentItem.duration && (
          <p className="text-cyan-400 text-xs mt-1">Custom duration: {currentItem.duration}s</p>
        )}
      </CardContent>
    </Card>
  );
};

export default LivePreview;
